import type { CSSProperties } from "react";

/**
 * Surface tokens for panel/card/menu chrome.
 *
 * Stroke is applied via Tailwind v4 utilities (`border-stroke-panel`,
 * `border-stroke-card`, `border-stroke-muted`, `border-stroke-background`)
 * defined in index.css `--color-stroke-*` → `--stroke-on-*`. Utilities-layer
 * classes override the base-layer `* { border-color: hsl(var(--border)) }`
 * reset by cascade order — no inline style is needed.
 */

/** Dropdown / context menu chrome — card radius + 6% stroke. */
export const MENU_SURFACE_CLASSES =
  "overflow-hidden rounded-lg border border-stroke-card bg-card text-foreground shadow-md";

/** Electron desktop window chrome — same stroke as sidebar `<Separator>`. */
export const APP_SHELL_RING_CLASSES =
  "ring-[0.5px] ring-[var(--stroke-on-panel)]";

/** Card/menu divider fill (horizontal rules). Use via `<Separator>` — not raw divs. */
export const DIVIDER_SURFACE_STYLE: CSSProperties = {
  backgroundColor: "hsl(var(--foreground) / 0.06)",
};

/** Inset divider for menus with `p-1.5` chrome. */
export const MENU_SEPARATOR_CLASSES = "-mx-1.5 my-1 h-px";

/** Panel surface — same shell as `<Card>` without subcomponents. */
export const PANEL_SURFACE_CLASSES =
  "overflow-hidden rounded-lg border border-stroke-panel bg-panel-subtle text-foreground";

/**
 * Marks a panel where form controls use `--input-surface` (canvas / background),
 * not transparent composer styling. Set on `<Card>` and other panel-subtle
 * form shells; paired with utilities.css `[data-form-surface]` overrides.
 */
export const FORM_SURFACE_ATTR = "data-form-surface";

/** Inset code / command blocks inside panels. */
export const CODE_BLOCK_SURFACE_CLASSES =
  "rounded-lg border border-stroke-panel bg-foreground/[0.04] p-4";

/**
 * @deprecated Add `border-stroke-panel` (or `border-stroke-card`) to className
 * instead — Tailwind utilities-layer classes override the base `*` rule by
 * cascade order. Removed once all consumers migrate.
 */
export const CARD_BORDER_STYLE: CSSProperties = {
  borderColor: "hsl(var(--foreground) / 0.06)",
};

/** @deprecated Alias of `CARD_BORDER_STYLE`. Use `border-stroke-panel` class. */
export const PANEL_DIVIDER_BORDER_STYLE = CARD_BORDER_STYLE;

/** @deprecated Use `border-stroke-panel` (or appropriate variant) class instead. */
export function withPanelBorder(style?: CSSProperties): CSSProperties {
  return { ...CARD_BORDER_STYLE, ...style };
}

/** @deprecated Use the `border-stroke-panel` class on the code block element. */
export function codeBlockSurfaceStyle(): CSSProperties {
  return CARD_BORDER_STYLE;
}
